import { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import { Paper, Box, IconButton } from '@mui/material';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import {
    BpmnPropertiesPanelModule,
    BpmnPropertiesProviderModule,
} from 'bpmn-js-properties-panel';
import EventBus from '@services/EventBus';
import API from '@utils/axios';
import { extractBestTranslation } from '@utils/helpers';
import aktorModdleDescriptor from './descriptors/aktor';
import multilangprocessPropertiesProviderModule from './provider/multilangprocess';
import messagePropertiesProviderModule from './provider/message';
import conditionPropertiesProviderModule from './provider/condition';
import externalServicePropertiesProviderModule from './provider/externalService';
import defaultFlowPropertiesProviderModule from './provider/defaultFlow';
import formPropertiesProviderModule from './provider/form';
import removePropertiesProviderModule from './provider/removeProperties';
import customEditorModule from './provider/customEditor';
import customTranslateFr from './customTranslate/customTranslateFr';
import customTranslateEn from './customTranslate/customTranslateEn';
import { useSnackbar } from '@contexts/SnackbarContext';

import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';
import '@bpmn-io/properties-panel/dist/assets/properties-panel.css';

const BpmnModelerComponent = forwardRef(({ bpmn, customerId }, ref) => {
    const { t, i18n } = useTranslation();
    const { showMessage } = useSnackbar();
    const bpmnModelerRef = useRef(null);
    const [isPropertiesOpen, setIsPropertiesOpen] = useState(true);

    const toggleProperties = () => {
        setIsPropertiesOpen(!isPropertiesOpen);
    };

    useEffect(() => {
        const customTranslate = i18n.language === 'fr' ? customTranslateFr : customTranslateEn;
        bpmnModelerRef.current = new BpmnModeler({
            container: '#js-canvas',
            propertiesPanel: {
                parent: '#properties'
            },
            additionalModules: [
                { translate: ['value', customTranslate] },
                BpmnPropertiesPanelModule,
                BpmnPropertiesProviderModule,
                removePropertiesProviderModule,
                customEditorModule,
                multilangprocessPropertiesProviderModule,
                messagePropertiesProviderModule,
                formPropertiesProviderModule,
                conditionPropertiesProviderModule,
                externalServicePropertiesProviderModule,
                defaultFlowPropertiesProviderModule
            ],
            moddleExtensions: {
                aktor: aktorModdleDescriptor
            },
        });

        const renderDiagram = async () => {
            try {
                await bpmnModelerRef.current.importXML(bpmn);
                const canvas = bpmnModelerRef.current.get('canvas');
                canvas.zoom('fit-viewport');
            } catch (err) {
                console.error('Erreur lors du chargement du diagramme:', err);
            }
        };

        renderDiagram();

        // Cleanup
        return () => {
            bpmnModelerRef.current.destroy();
        };
    }, [i18n.language, bpmn]);

    // Charger en ajax les actions disponibles pour ce customer,
    // et les mettre à disposition de bpmn-js via EventBus
    useEffect(() => {
        const fetchServiceTypes = async () => {

            // Aucun service disponible si pas de client sélectionné
            if (!customerId || customerId === '-') {
                EventBus.emit('updateServices', []);
                return;
            }

            try {
                const response = await API.get(`/customers/${customerId}/services/actions`);

                // Afficher les erreurs s'il y en a
                if (response.data.errors && response.data.errors.length > 0) {
                    response.data.errors.forEach(error => {
                        showMessage(t('Unable to contact {{service}}. Some actions involving external services will be unavailable.', { service: error }), 'error');
                    });
                }

                // Traduire les actions, paramètres, enum, etc.
                let translatedServices = {};
                Object.keys(response.data.actions).forEach(service => {
                    let translatedParams = [];
                    if (response.data.actions[service].params) {
                        response.data.actions[service].params.forEach(param => {
                            let translatedParam = {
                                name: param.name || '',
                                type: param.type || 'text',
                                required: param.required || false,
                                defaultValue: param.defaultValue || null
                            };
                            if (param.label) {
                                translatedParam.label = extractBestTranslation(param.label, i18n.language);
                            }
                            if (param.helperText) {
                                translatedParam.helperText = extractBestTranslation(param.helperText, i18n.language);
                            }
                            if (param.enum) {
                                let translatedEnum = {};
                                Object.keys(param.enum).forEach(key => {
                                    translatedEnum[key] = extractBestTranslation(param.enum[key], i18n.language);
                                });
                                translatedParam.enum = translatedEnum;
                            }
                            translatedParams.push(translatedParam);
                        });
                    }
                    translatedServices[service] = {
                        label: extractBestTranslation(response.data.actions[service].label, i18n.language),
                        helperText: extractBestTranslation(response.data.actions[service].helperText || '', i18n.language),
                        type: response.data.actions[service].type,
                        params: translatedParams,
                        data: response.data.actions[service].data || {},
                    };
                });

                EventBus.emit('updateServices', translatedServices);
                EventBus.emit('updateOriginalServices', response.data.actions);

            } catch (error) {
                console.error('Erreur lors du chargement des actions des services externes :', error);
                showMessage(t('Unable to contact {{service}}. Some actions involving external services will be unavailable.', { service: 'Kampaign API' }), 'error');
            }
        };

        if (customerId) {
            fetchServiceTypes();
        }
    }, [customerId, i18n.language, showMessage, t]);

    useImperativeHandle(ref, () => ({
        exportDiagram: async () => {
            try {
                const { xml } = await bpmnModelerRef.current.saveXML({ format: true });
                return xml;
            }
            catch (err) {
                console.error('Erreur lors de l\'export du diagramme:', err);
                return null;
            }
        }
    }));

    return (
        <Box sx={{
            display: 'grid',
            gridTemplateColumns: isPropertiesOpen ? '1fr 250px' : '1fr 0px',
            height: '100%',
            position: 'relative'
        }}>
            <Paper
                id="js-canvas"
                sx={{
                    width: '100%',
                    height: '100%'
                }}
            >
            </Paper>
            <IconButton
                onClick={toggleProperties}
                sx={{
                    position: 'absolute',
                    top: '12px',
                    right: isPropertiesOpen ? '262px' : '12px',
                    zIndex: 100,
                    backgroundColor: 'white',
                    borderRadius: '50%',
                    boxShadow: 1,
                }}
            >
                {isPropertiesOpen ? <ArrowForwardIosIcon /> : <ArrowBackIosIcon />}
            </IconButton>
            <Paper
                id="properties"
                sx={{
                    height: '100%',
                    overflow: 'auto',
                }}
            ></Paper>
        </Box>
    );
});

export default BpmnModelerComponent;
